/**
 * Ersteinrichtung (UX-Welle 2): Was ein neues Konto braucht, damit die App
 * tut, wofür sie da ist — erinnern, rechnen, planen.
 *
 * Der Fortschritt wird **aus dem echten Zustand abgeleitet**, nicht als
 * „Schritt erledigt" gespeichert: Wer die Zielwerte in den Einstellungen
 * setzt, hat den Schritt erledigt, egal von wo. Gespeichert wird nur, ob die
 * Kachel ausgeblendet ist — je Gerät, denn Installation und Mitteilungen sind
 * es auch.
 */

import type { Module } from '@kraftwerk/shared';
import type { PushStatus } from './push/usePush.js';
import { db } from './db/local.js';
import type { KraftwerkDatabase } from './db/local.js';

export type SetupStep = 'modules' | 'install' | 'push' | 'body' | 'training';
export const SETUP_STEPS: readonly SetupStep[] = ['modules', 'install', 'push', 'body', 'training'];

export interface SetupFacts {
  /** Module gewählt (E15)? `profiles.modules` nicht mehr `null`. */
  modulesChosen: boolean;
  /** Was gilt — bestimmt, ob „Körper und Ziel“ und „Training“ dazugehören. */
  modules: readonly Module[];
  standalone: boolean;
  pushStatus: PushStatus;
  hasTargets: boolean;
  hasActivePlan: boolean;
  hasTrainingDays: boolean;
}

export interface SetupStepState {
  step: SetupStep;
  done: boolean;
}

export interface SetupStatus {
  /** Nur die Schritte, die auf diesem Gerät Sinn haben. */
  steps: SetupStepState[];
  doneCount: number;
  next: SetupStep | null;
  complete: boolean;
}

/**
 * Welche Schritte gelten und welche erledigt sind.
 *
 * - **Installieren** gilt nur, wo es Voraussetzung für Mitteilungen ist
 *   (iPhone/iPad im Browser) — oder schon erledigt ist. Am Rechner wäre es
 *   ein Schritt ohne Nutzen.
 * - **Mitteilungen** fallen weg, wo es sie nicht gibt (kein Push im Browser,
 *   Server ohne Schlüssel). Abgelehnt bleibt der Schritt offen: Die App kann
 *   dann nicht erinnern, und das soll man sehen.
 */
export function setupStatus(facts: SetupFacts): SetupStatus {
  const steps: SetupStepState[] = [{ step: 'modules', done: facts.modulesChosen }];
  if (facts.standalone || facts.pushStatus === 'needs-install') {
    steps.push({ step: 'install', done: facts.standalone });
  }
  if (facts.pushStatus !== 'unsupported' && facts.pushStatus !== 'not-configured') {
    steps.push({ step: 'push', done: facts.pushStatus === 'enabled' });
  }
  // Ziele gehören zur Ernährung, Plan und Tage zum Training — wer das Modul
  // nicht nutzt, wird nicht danach gefragt.
  if (facts.modules.includes('nutrition')) {
    steps.push({ step: 'body', done: facts.hasTargets });
  }
  if (facts.modules.includes('training')) {
    steps.push({ step: 'training', done: facts.hasActivePlan && facts.hasTrainingDays });
  }
  const doneCount = steps.filter((entry) => entry.done).length;
  return {
    steps,
    doneCount,
    next: steps.find((entry) => !entry.done)?.step ?? null,
    complete: doneCount === steps.length,
  };
}

const HIDDEN_KEY = 'setupHidden';

export async function readSetupHidden(database: KraftwerkDatabase = db): Promise<boolean> {
  return (await database.meta.get(HIDDEN_KEY))?.value === 'ja';
}

export interface SetupVisibilityFacts {
  /** Gespeicherte Wahl dieses Geräts — `undefined`, solange nicht entschieden. */
  stored: 'ja' | 'nein' | undefined;
  /** Schon einmal abgeglichen? Vorher sähe jedes Konto neu aus. */
  synced: boolean;
  /** Trainings, Mahlzeiten oder Tagebuch vorhanden. */
  history: boolean;
}

/**
 * Die Kachel ist für **neue** Konten. Wer die App seit Wochen nutzt, soll
 * nach einem Update nicht „In ein paar Minuten startklar“ lesen — die
 * Einrichtung bleibt für ihn über die Einstellungen erreichbar.
 *
 * Entschieden wird je Gerät einmal, und erst nach dem ersten Abgleich: Auf
 * einem frischen Gerät ist die lokale Ablage leer, auch bei einem alten Konto.
 * `null` heißt „noch nicht entscheidbar“ — dann zeigt die App nichts.
 */
export function setupHiddenFrom(facts: SetupVisibilityFacts): boolean | null {
  if (facts.stored) return facts.stored === 'ja';
  if (!facts.synced) return null;
  return facts.history;
}

export async function readSetupVisibility(
  database: KraftwerkDatabase = db,
): Promise<SetupVisibilityFacts> {
  const [stored, cursors, sessions, meals, entries] = await Promise.all([
    database.meta.get(HIDDEN_KEY),
    database.meta.where('key').startsWith('cursor:').count(),
    database.workout_sessions.count(),
    database.food_log.count(),
    database.journal_entries.count(),
  ]);
  return {
    stored: stored?.value === 'ja' || stored?.value === 'nein' ? stored.value : undefined,
    synced: cursors > 0,
    history: sessions + meals + entries > 0,
  };
}

export async function writeSetupHidden(
  hidden: boolean,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: HIDDEN_KEY, value: hidden ? 'ja' : 'nein' });
}

// --- Installieren auf Android/Chrome ----------------------------------------

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

let deferredInstall: InstallPromptEvent | null = null;

/**
 * Chrome bietet die Installation per Ereignis an; aufgehoben wird es für den
 * Knopf im Schritt „Installieren". iOS kennt das nicht — dort erklärt die App
 * den Weg über das Teilen-Menü.
 */
export function captureInstallPrompt(target: Window = window): () => void {
  const onPrompt = (event: Event) => {
    event.preventDefault();
    deferredInstall = event as InstallPromptEvent;
  };
  target.addEventListener('beforeinstallprompt', onPrompt);
  return () => target.removeEventListener('beforeinstallprompt', onPrompt);
}

export function canPromptInstall(): boolean {
  return deferredInstall !== null;
}

export async function promptInstall(): Promise<void> {
  const event = deferredInstall;
  deferredInstall = null;
  await event?.prompt();
}
